import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import surveyService from "../../Services/surveyService";
import NormalAlert from "../Alerts/NormalAlert";
import JoditEditor from "jodit-react";
import { useRef } from "react";

export default function CreateSurvey({ survey }) {
  const [surveyName, setSurveyName] = useState("");
  const [description, setDescription] = useState("");
  const [caseStudyFile, setCaseStudyFile] = useState(null);
  const [totalTime, setTotalTime] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [errors, setErrors] = useState({});
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const editorRef = useRef(null);

  //Alert
  const [alert, setAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  const navigate = useNavigate();

  const descriptionEditorConfig = {
    height: 220,
    readonly: false,
    toolbar: true,
    buttons: "bold,italic,underline,strikethrough,|,ul,ol,|,link,|,undo,redo",
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    placeholder: "Enter Description",
  };

  const stripHtml = (html = "") => {
    return html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!surveyName.trim()) newErrors.surveyName = "Survey Name is required.";
    const plainDescription = stripHtml(description);
    if (!plainDescription) {
      newErrors.description = "Description is required.";
    }
    if (!caseStudyFile) {
      newErrors.caseStudy = "Case Study PDF is required.";
    } else if (caseStudyFile.size > 10 * 1024 * 1024) {
      newErrors.caseStudy = "File size must not exceed 10 MB.";
    } else if (caseStudyFile.type !== "application/pdf") {
      newErrors.caseStudy = "Only PDF files are allowed.";
    }
    if (
      parseInt(days || 0) === 0 &&
      parseInt(hours || 0) === 0 &&
      parseInt(minutes || 0) === 0
    ) {
      newErrors.totalTime = "Total time cannot be 0.";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    const totalMinutes =
      parseInt(days || 0) * 24 * 60 +
      parseInt(hours || 0) * 60 +
      parseInt(minutes || 0);
    const totalMinutesText = `${totalMinutes} minutes`;

    const formData = new FormData();
    formData.append("Token", localStorage.getItem("JWTToken") || "");
    formData.append("SurveyName", surveyName);
    formData.append("SurveyDescription", description);
    formData.append("TotalTimeForSurvey", totalMinutesText);
    formData.append("CaseStudy", caseStudyFile);
    formData.append("SurveyThumbnail", thumbnail);
    formData.append("ShuffleQuestions", shuffleQuestions ? 1 : 0);

    try {
      const result = await surveyService.createSurvey(formData);

      if (result.status === "Success") {
        showAlert("success", result.message);
        // navigate("/survey-list");
        setTimeout(() => {
          navigate("/survey-list", { state: { defaultTab: "Draft" } });
        }, 3000);
      } else {
        showAlert("error", result.message || "Failed to create survey.");
      }
    } catch (err) {
      console.error(err);
      showAlert("error", "Something went wrong while creating the survey.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSurveyName("");
    setDescription("");
    setCaseStudyFile("");
    setShuffleQuestions(false);
    setDays(0);
    setHours(0);
    setMinutes(0);
    setThumbnail(null);
    setErrors({});
  };

  return (
    <>
      <div className="min-h-screen flex flex-col items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-4xl bg-white/95 backdrop-blur-lg shadow-2xl rounded-3xl p-12 border border-gray-200"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-extrabold text-[#3bb290] mb-3">
              Create Survey
            </h1>
            <p className="text-gray-500 text-lg">
              Fill out the details below to create a new survey
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Survey Name */}
            <div className="relative group">
              <label className="block text-gray-700 font-semibold mb-2">
                Survey Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={surveyName}
                onChange={(e) => setSurveyName(e.target.value)}
                placeholder="Enter Survey Name"
                className={`w-full border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all group-hover:scale-[1.02]
                ${errors.surveyName ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.surveyName && (
                <p className="text-red-500 text-sm mt-1">{errors.surveyName}</p>
              )}
            </div>
            {/* Description */}
            <div className="relative group">
              <label className="block text-gray-700 font-semibold mb-2">
                Description <span className="text-red-600">*</span>
              </label>

              <div
                className={`rounded-xl border transition-all group-hover:scale-[1.02]
      ${errors.description ? "border-red-500" : "border-gray-300"}`}
              >
                <JoditEditor
                  ref={editorRef}
                  value={description || ""}
                  config={descriptionEditorConfig}
                  onBlur={(content) => {
                    setDescription(content);
                  }}
                  onChange={() => {
                    if (errors.description) {
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }
                  }}
                />
              </div>

              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description}
                </p>
              )}
            </div>

            {/* Case Study PDF */}
            <div className="relative group">
              <label className="block text-gray-700 font-semibold mb-2">
                Case Study PDF <span className="text-red-600">*</span>
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setCaseStudyFile(e.target.files[0])}
                className={`w-full border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290]
      ${errors.caseStudy ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.caseStudy && (
                <p className="text-red-500 text-sm mt-1">{errors.caseStudy}</p>
              )}
            </div>

            <div className="relative group mt-6">
              <label className="block text-gray-700 font-semibold mb-2">
                Shuffle Questions
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shuffleQuestions"
                    value="true"
                    checked={shuffleQuestions === true}
                    onChange={() => setShuffleQuestions(true)}
                    className="h-4 w-4 text-[#3bb290] focus:ring-[#3bb290]"
                  />
                  <span className="text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shuffleQuestions"
                    value="false"
                    checked={shuffleQuestions === false}
                    onChange={() => setShuffleQuestions(false)}
                    className="h-4 w-4 text-[#3bb290] focus:ring-[#3bb290]"
                  />
                  <span className="text-gray-700">No</span>
                </label>
              </div>

              {errors.shuffleQuestions && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.shuffleQuestions}
                </p>
              )}
            </div>

            {/* Total Time */}
            <div className="relative group">
              <label className="text-gray-700 font-semibold mb-2">
                Total Time For Survey <span className="text-red-600">*</span>
              </label>
              <div className="flex space-x-4">
                {/* Days */}
                <div>
                  <label>Days</label>
                  <input
                    type="number"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    className="w-full mt-2 border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all group-hover:scale-[1.02]"
                    min="0"
                  />
                </div>
                {/* Hours */}
                <div>
                  <label>Hours</label>
                  <input
                    type="number"
                    value={hours}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val === "") {
                        setHours("");
                        return;
                      }
                      val = Math.min(Math.max(Number(val), 0), 23); // clamp between 0-23
                      setHours(val);
                    }}
                    className="w-full mt-2 border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all group-hover:scale-[1.02]"
                  />
                </div>

                {/* Minutes */}
                <div>
                  <label>Minutes</label>
                  <input
                    type="number"
                    value={minutes}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val === "") {
                        setMinutes("");
                        return;
                      }
                      val = Math.min(Math.max(Number(val), 0), 59); // clamp between 0-59
                      setMinutes(val);
                    }}
                    className="w-full mt-2 border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all group-hover:scale-[1.02]"
                  />
                </div>
              </div>
              {errors.totalTime && (
                <p className="text-red-500 text-sm mt-1">{errors.totalTime}</p>
              )}
            </div>

            {/* Survey Thumbnail */}
            <div className="relative group">
              <label className="block text-gray-700 font-semibold mb-2">
                Survey Thumbnail
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnail(e.target.files[0])}
                className="w-full border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290]"
              />
            </div>

            {/* Submit */}
            <div className="flex justify-center mt-6 space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="bg-gradient-to-r from-[#3bb290] to-[#1f7d6b] text-white font-bold px-12 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-transform"
              >
                Create Survey
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={handleReset}
                className="bg-gray-300 text-gray-800 font-bold px-12 py-4 rounded-2xl shadow-md hover:shadow-xl transition-transform"
              >
                Reset
              </motion.button>
            </div>
          </form>
        </motion.div>

        {isLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity">
            <div className="flex flex-col items-center gap-4">
              {/* Spinner */}
              <div className="h-16 w-16 rounded-full border-4 border-white/30 border-t-white animate-spin" />

              {/* Text */}
              <p className="text-white text-lg font-medium tracking-wide">
                Creating Survey...
              </p>
            </div>
          </div>
        )}
      </div>
      <NormalAlert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert({ ...alert, show: false })}
      />
    </>
  );
}
